import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const REVIEWS_COLLECTION = 'course_reviews';
const COURSES_COLLECTION = 'courses';

/**
 * Get reviews for a course (for display on course details).
 */
export async function getReviewsForCourse(courseId) {
  if (!courseId) return [];
  const q = query(collection(db, REVIEWS_COLLECTION), where('courseId', '==', courseId));
  const snap = await getDocs(q);
  const list = [];
  snap.forEach((docSnap) => {
    const d = docSnap.data();
    list.push({
      id: docSnap.id,
      userId: d.userId,
      userName: d.userName || 'User',
      rating: d.rating ?? 0,
      review: d.review || '',
      createdAt: d.createdAt,
    });
  });
  list.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return list;
}

/**
 * Get current user's review for a course (if any).
 */
export async function getMyReviewForCourse(userId, courseId) {
  if (!userId || !courseId) return null;
  const q = query(
    collection(db, REVIEWS_COLLECTION),
    where('courseId', '==', courseId),
    where('userId', '==', userId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  const d = docSnap.data();
  return {
    id: docSnap.id,
    rating: d.rating ?? 0,
    review: d.review || '',
  };
}

/**
 * Submit or update a review. Updates course's rating and review_count.
 */
export async function submitReview(userId, userName, courseId, rating, review) {
  if (!userId || !courseId || rating == null) throw new Error('Missing required fields');
  const ratingNum = Math.max(1, Math.min(5, Number(rating)));
  const reviewsRef = collection(db, REVIEWS_COLLECTION);
  const q = query(reviewsRef, where('courseId', '==', courseId), where('userId', '==', userId));
  const existingSnap = await getDocs(q);
  const reviewId = existingSnap.empty ? null : existingSnap.docs[0].id;

  if (existingSnap.empty) {
    await addDoc(reviewsRef, {
      courseId,
      userId,
      userName: userName || 'User',
      rating: ratingNum,
      review: String(review || '').trim(),
      createdAt: serverTimestamp(),
    });
  } else {
    const ref = doc(db, REVIEWS_COLLECTION, reviewId);
    await updateDoc(ref, {
      rating: ratingNum,
      review: String(review || '').trim(),
      updatedAt: serverTimestamp(),
    });
  }

  const allSnap = await getDocs(query(collection(db, REVIEWS_COLLECTION), where('courseId', '==', courseId)));
  let sum = 0;
  let count = 0;
  allSnap.forEach((d) => {
    const data = d.data();
    sum += data.rating ?? 0;
    count += 1;
  });
  const avgRating = count > 0 ? Math.round((sum / count) * 10) / 10 : 0;
  const courseRef = doc(db, COURSES_COLLECTION, courseId);
  await updateDoc(courseRef, {
    rating: avgRating,
    review_count: count,
    updatedAt: serverTimestamp(),
  });
}
